import { useEffect, useState } from "react";
import { Link } from "wouter";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import Seo from "@/components/Seo";
import PowloLayout from "@/components/powlo/PowloLayout";
import { FAQ_CATEGORIES } from "@/data/faqContent";
import { TRADING_NAME } from "@/constants/company";

export default function Faq() {
  useEffect(() => {
    scrollToPageTop();
  }, []);

  return (
    <PowloLayout>
      <Seo
        title="FAQ"
        description={`Everything you need to know about solar, the ${TRADING_NAME} platform, and the installation process.`}
        path="/faq"
      />
      <section className="mx-auto flex max-w-3xl flex-col gap-4 px-5 pb-8 pt-14 sm:px-8">
        <div className="text-sm font-bold tracking-widest text-indigo">FAQ</div>
        <h1 className="m-0 font-display text-4xl font-bold leading-tight tracking-tight text-ink sm:text-6xl">
          Frequently asked questions
        </h1>
        <p className="m-0 max-w-xl text-lg leading-relaxed text-[#2A2A33]">
          Everything you need to know about solar, the {TRADING_NAME} platform, and the installation process.
        </p>
      </section>

      <div className="mx-auto flex max-w-3xl flex-col gap-10 px-5 pb-16 sm:px-8">
        {FAQ_CATEGORIES.map((section) => (
          <section key={section.category} className="flex flex-col gap-4">
            <h2 className="m-0 text-sm font-bold tracking-widest text-indigo">{section.category.toUpperCase()}</h2>
            <CategoryAccordion items={section.items} />
          </section>
        ))}

        <section className="flex flex-col items-start gap-4 rounded-2xl border border-border bg-white p-8">
          <div>
            <h2 className="m-0 font-display text-2xl font-bold tracking-tight text-ink">Still have questions?</h2>
            <p className="m-0 mt-1 text-base leading-relaxed text-[#2A2A33]">
              Our team is happy to help. Get in touch and we&apos;ll respond within 1 business day.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              href="/contact"
              className="inline-flex h-12 items-center justify-center rounded-2xl bg-lime px-6 font-display text-base font-bold tracking-tight text-ink no-underline"
            >
              Contact us
            </Link>
            <Link
              href="/quote"
              className="inline-flex h-12 items-center justify-center rounded-2xl border-2 border-ink px-6 font-display text-base font-bold tracking-tight text-ink no-underline"
            >
              Get a free quote
            </Link>
          </div>
        </section>
      </div>
    </PowloLayout>
  );
}

function CategoryAccordion({ items }: { items: { question: string; answer: string }[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, index) => {
        const open = openIndex === index;
        return (
          <div key={item.question} className="rounded-2xl border border-border bg-white">
            <button
              type="button"
              onClick={() => setOpenIndex(open ? null : index)}
              aria-expanded={open}
              className="flex min-h-[58px] w-full items-center justify-between gap-4 px-6 py-4 text-left text-base font-bold text-ink"
            >
              <span>{item.question}</span>
              <span aria-hidden="true" className="shrink-0 text-xl text-indigo">
                {open ? "−" : "+"}
              </span>
            </button>
            {open && (
              <div className="m-0 px-6 pb-5 text-base leading-relaxed text-[#2A2A33]">{item.answer}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
